import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@bymason-private/auth-provider';
import { Box } from '@bymason-private/legacy-pattern-components';
import { useToast } from '../../';
import { NotificationModel, useGetNotifications } from '@bymason/controller-api-hooks';
import { CreateEditNotificationModal } from '../modals/create-edit-notification-modal';
import { DeleteNotificationModal } from '../modals/delete-notification-modal';
import { NotificationsList } from './notifications-list';

interface NotificationsViewProps {
  showCreateNotificationModal?: boolean;
  onCloseCreateNotificationModal?: () => void;
  emptyStateComponent?: JSX.Element;
}

export function NotificationsView({
  showCreateNotificationModal = false,
  onCloseCreateNotificationModal,
  emptyStateComponent,
}: NotificationsViewProps) {
  const { authToken = '' } = useAuth();
  const [setToastMessage, setToastSeverity] = useToast();

  const [getNotifications, notifications, notificationsError, notificationBusy] = useGetNotifications(
    authToken
  );

  const [showEditNotificationModal, setShowEditNotificationModal] = useState(false);
  const [showDeleteNotificationModal, setShowDeleteNotificationModal] = useState(false);
  const [notificationModalTarget, setNotificationModalTarget] = useState<NotificationModel>();

  const handleModalClose = useCallback(() => {
    setNotificationModalTarget(undefined);
    setShowEditNotificationModal(false);
    setShowDeleteNotificationModal(false);
    onCloseCreateNotificationModal && onCloseCreateNotificationModal();
  }, [onCloseCreateNotificationModal]);

  const handleCreateNotificationSuccess = useCallback(
    (newNotification, withError) => {
      setToastSeverity(withError ? 'warning' : 'success');
      setToastMessage(
        `Sucessfully ${notificationModalTarget ? 'updated' : 'created'} rule "${newNotification.name}"${
          withError ? ', but you may see a duplicate rule. Please try deleting again' : ''
        }`
      );
      getNotifications({ metric: 'usageRateMonthly' });
    },
    [getNotifications, notificationModalTarget, setToastMessage, setToastSeverity]
  );

  const handleDeleteNotificationSuccess = useCallback(
    (newNotification) => {
      setToastSeverity('success');
      setToastMessage(`Sucessfully deleted rule "${newNotification.name}"`);
      getNotifications({ metric: 'usageRateMonthly' });
    },
    [getNotifications, setToastMessage, setToastSeverity]
  );

  const handleChangeNotificationError = useCallback(
    (message) => {
      setToastSeverity('error');
      setToastMessage(message);
    },
    [setToastMessage, setToastSeverity]
  );

  const handleEditNotification = (notification: NotificationModel) => {
    setNotificationModalTarget(notification);
    setShowEditNotificationModal(true);
  };

  const handleDeleteNotification = (notification: NotificationModel) => {
    setNotificationModalTarget(notification);
    setShowDeleteNotificationModal(true);
  };

  useEffect(() => {
    if (showCreateNotificationModal) {
      setNotificationModalTarget(undefined);
    }
  }, [showCreateNotificationModal]);

  useEffect(() => {
    getNotifications({ metric: 'usageRateMonthly' });
  }, [getNotifications]);

  useEffect(() => {
    if (notificationsError && !notificationBusy) {
      setToastMessage(notificationsError);
      setToastSeverity('error');
    }
  }, [notificationsError, notificationBusy, setToastMessage, setToastSeverity]);

  return (
    <Box>
      {(notifications?.data?.length || !emptyStateComponent) && (
        <NotificationsList
          notifications={notifications?.data || []}
          onDeleteNotification={handleDeleteNotification}
          onEditNotification={handleEditNotification}
        />
      )}
      {!notificationBusy && !notifications?.data?.length && emptyStateComponent}

      {(showEditNotificationModal || showCreateNotificationModal) && (
        <CreateEditNotificationModal
          open={showEditNotificationModal || showCreateNotificationModal}
          notification={notificationModalTarget}
          onClose={handleModalClose}
          onSuccess={handleCreateNotificationSuccess}
          onError={handleChangeNotificationError}
        />
      )}

      {showDeleteNotificationModal && notificationModalTarget && (
        <DeleteNotificationModal
          open={showDeleteNotificationModal}
          onClose={handleModalClose}
          notification={notificationModalTarget}
          onSuccess={handleDeleteNotificationSuccess}
          onError={handleChangeNotificationError}
        />
      )}
    </Box>
  );
}

export { useGetNotifications };
